import { formatDate, formatPeriod } from '../../components/EngagementHub/format';
import { type PartyIdentityView, UNKNOWN_PARTY } from '../parties';
import type { HoursSubmissionView } from '../viewModel';
import HoursDescriptionCell from './HoursDescriptionCell';
import HoursRowActions, { type HoursRowAction } from './HoursRowActions';
import HoursStatusCell from './HoursStatusCell';
import { CELL, DASH, MUTED_CELL, MUTED_DASH, hoursColumns } from './HoursTable';
import { PARTIAL_APPROVED_STATUSES } from './partialConfirm';

interface HoursRowProps {
  row: HoursSubmissionView;
  showPaid: boolean;
  /** `partyIdentity` over this engagement, for the Approver cell. */
  identify: (userId: string) => PartyIdentityView | null;
  canConfirm: boolean;
  canResolve: boolean;
  maxConfirmableHours: number;
  expertName: string;
}

/** The statuses that approved at least part of a submission. */
const APPROVED_STATUSES = new Set(['confirmed', ...PARTIAL_APPROVED_STATUSES]);

/**
 * The person who approved the row: the direct confirmer, or the resolver when
 * a dispute ended in a confirmation. Only an approved row names anyone: whoever
 * rejected or withdrew a row did not approve it, and nobody has yet on a row
 * still awaiting a decision. A deemed approval names nobody either (its actor
 * is `system`), and neither does an id no table can name.
 */
function approverName(row: HoursSubmissionView, identify: HoursRowProps['identify']): string {
  if (row.approvedAutomatically || !APPROVED_STATUSES.has(row.status)) return DASH;
  const actorId = row.confirmedByUserId ?? row.resolvedByUserId;
  const party = actorId ? identify(actorId) : null;
  return party && party !== UNKNOWN_PARTY ? party.name : DASH;
}

/**
 * Which action the row offers, if any. The gates say whether this viewer may
 * ever act; the status says whether this row can be acted on now.
 * `partially_confirmed` offers nothing: the domain has no edge out of it until
 * the partial-hours fork is ruled on.
 */
function rowAction(
  row: HoursSubmissionView,
  canConfirm: boolean,
  canResolve: boolean
): HoursRowAction | null {
  if (canConfirm && row.status === 'submitted') return 'confirm';
  if (canResolve && row.status === 'escalated') return 'resolve';
  return null;
}

export default function HoursRow({
  row,
  showPaid,
  identify,
  canConfirm,
  canResolve,
  maxConfirmableHours,
  expertName,
}: HoursRowProps): JSX.Element {
  const action = rowAction(row, canConfirm, canResolve);
  const period =
    row.periodStart && row.periodEnd ? formatPeriod(row.periodStart, row.periodEnd) : MUTED_DASH;

  return (
    <>
      <tr className={`align-top ${action ? '' : 'border-b border-surface-border'}`}>
        <td className={`${CELL} whitespace-nowrap`}>{period}</td>
        <td className={`${CELL} text-right tabular-nums`}>{row.hoursSubmitted.toFixed(2)}</td>
        <td className={`${CELL} text-right tabular-nums`}>
          {row.hoursConfirmed === null ? MUTED_DASH : row.hoursConfirmed.toFixed(2)}
        </td>
        <td className={CELL}>
          <HoursStatusCell row={row} />
        </td>
        <td className={`${CELL} min-w-[240px] max-w-[420px]`}>
          <HoursDescriptionCell row={row} />
        </td>
        <td className={MUTED_CELL}>{approverName(row, identify)}</td>
        {showPaid ? (
          <td className={MUTED_CELL}>{row.paidAt ? formatDate(row.paidAt) : DASH}</td>
        ) : null}
      </tr>
      {action ? (
        <HoursRowActions
          action={action}
          row={row}
          columnCount={hoursColumns(showPaid).length}
          maxConfirmableHours={maxConfirmableHours}
          expertName={expertName}
        />
      ) : null}
    </>
  );
}
